import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, IconButton, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { CloseSquare, Edit2, TickSquare } from "iconsax-react";
import { Delete } from "react-iconly";
import { toast } from "react-toastify";
import SearchBar from "../../../components/SearchBar";
import DataGrid from "../../../components/DataGrid";
import DeleteSymbolDialog from "./components/DeleteSymbolDialog";
import NewSymbolDialog, { type UploadedSymbolDraft } from "./components/NewSymbolDialog";
import RejectSymbolDialog from "./components/RejectSymbolDialog";
import RenameSymbolDialog from "./components/RenameSymbolDialog";
import { technicalEstimateLegendService, fileToBase64 } from "../../../services";
import type { CompanyLegendSymbolSummaryDto, CreateCompanySymbolItemDto } from "../../../services";
import type { EngineerSymbolRow, SymbolStatus } from "./types";
import { RADIUS } from "../../../theme/mixins";
import { formatDate } from "../../../utils/formatUtils";

const formatLastUpdated = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return formatDate(date);
};

const toEngineerSymbolRow = (dto: CompanyLegendSymbolSummaryDto): EngineerSymbolRow => ({
  id: dto.id,
  symbolImage: dto.iconBlobPath ?? "",
  description: dto.name,
  vermogenscode: "",
  createdBy: dto.createdBy,
  status: dto.status,
  lastUpdated: formatLastUpdated(dto.lastUpdatedDate),
});

interface IngenieurslegendeProps {
  companyId?: string;
}

const Ingenieurslegende: FC<IngenieurslegendeProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const STATUS_CONFIG: Record<
    SymbolStatus,
    { label: string; color: "success" | "error" | "default"; tooltip: string }
  > = {
    approved: {
      label: t("configuration.ingenieurslegende.status.approved"),
      color: "success",
      tooltip: t("configuration.ingenieurslegende.status.approvedTooltip"),
    },
    rejected: {
      label: t("configuration.ingenieurslegende.status.rejected"),
      color: "error",
      tooltip: t("configuration.ingenieurslegende.status.rejectedTooltip"),
    },
    pending: {
      label: t("configuration.ingenieurslegende.status.pending"),
      color: "default",
      tooltip: t("configuration.ingenieurslegende.status.pendingTooltip"),
    },
  };
  const [rows, setRows] = useState<EngineerSymbolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [symbolToDelete, setSymbolToDelete] = useState<string | null>(null);
  const [symbolToReject, setSymbolToReject] = useState<string | null>(null);
  const [symbolToRename, setSymbolToRename] = useState<EngineerSymbolRow | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [newSymbolDialogOpen, setNewSymbolDialogOpen] = useState(false);
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);

  // `silent` skips the loading-skeleton flash for background refreshes (e.g. after
  // approve/reject) - toggling loading there was what reset the grid's scroll to top.
  const fetchSymbols = useCallback(async (silent = false) => {
    if (!companyId) {
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      const symbols = await technicalEstimateLegendService.getByCompanyId(companyId);
      // Sorted by description (not lastUpdatedDate) so a row's position doesn't
      // shift just because it was approved/rejected.
      const mapped = symbols
        .map(toEngineerSymbolRow)
        .sort((a, b) => a.description.localeCompare(b.description));
      setRows(mapped);
    } catch (err) {
      console.error("Failed to fetch legend symbols:", err);
      toast.error(t("configuration.ingenieurslegende.loadSymbolsFailed"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchSymbols();
  }, [fetchSymbols]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (row) =>
        row.description.toLowerCase().includes(term) ||
        STATUS_CONFIG[row.status].label.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const setApproval = async (id: string, action: "Approve" | "Reject") => {
    setProcessingId(id);
    const scroller = gridWrapperRef.current?.querySelector<HTMLDivElement>(
      ".MuiDataGrid-virtualScroller"
    );
    const scrollTop = scroller?.scrollTop ?? null;
    try {
      await technicalEstimateLegendService.setApproval(id, action);
      toast.success(
        action === "Approve"
          ? t("configuration.ingenieurslegende.approvedToast")
          : t("configuration.ingenieurslegende.rejectedToast")
      );
      await fetchSymbols(true);
      if (scrollTop !== null) {
        requestAnimationFrame(() => {
          const el = gridWrapperRef.current?.querySelector<HTMLDivElement>(
            ".MuiDataGrid-virtualScroller"
          );
          if (el) el.scrollTop = scrollTop;
        });
      }
    } catch (err) {
      console.error(`Failed to ${action.toLowerCase()} symbol:`, err);
      toast.error(
        action === "Approve"
          ? t("configuration.ingenieurslegende.approveFailedToast")
          : t("configuration.ingenieurslegende.rejectFailedToast")
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprove = (id: string) => {
    setApproval(id, "Approve");
  };

  const handleRejectConfirm = () => {
    if (!symbolToReject) return;
    const id = symbolToReject;
    setSymbolToReject(null);
    setApproval(id, "Reject");
  };

  const handleDeleteConfirm = async () => {
    if (!symbolToDelete) return;
    const id = symbolToDelete;
    setSymbolToDelete(null);
    try {
      await technicalEstimateLegendService.deleteSymbol(id);
      toast.success(t("configuration.ingenieurslegende.deletedToast"));
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      console.error("Failed to delete symbol:", err);
      toast.error(t("configuration.ingenieurslegende.deleteFailedToast"));
    }
  };

  const handleRenameSave = async (description: string) => {
    if (!symbolToRename || !companyId) return;
    setRenaming(true);
    try {
      await technicalEstimateLegendService.updateSymbol(symbolToRename.id, companyId, description);
      toast.success(t("configuration.ingenieurslegende.renamedToast"));
      setSymbolToRename(null);
      await fetchSymbols();
    } catch (err) {
      console.error("Failed to rename symbol:", err);
      toast.error(t("configuration.ingenieurslegende.renameFailedToast"));
    } finally {
      setRenaming(false);
    }
  };

  const handleNewSymbolsSave = async (drafts: UploadedSymbolDraft[]) => {
    if (!companyId) {
      toast.error(t("configuration.ingenieurslegende.companyMissingToast"));
      throw new Error("Missing companyId");
    }

    const items: CreateCompanySymbolItemDto[] = [];
    for (const draft of drafts) {
      const name = draft.description.trim();
      if (!name) {
        toast.error(t("configuration.ingenieurslegende.descriptionRequiredToast"));
        throw new Error("Missing description");
      }
      items.push({
        base64File: await fileToBase64(draft.file),
        fileName: draft.file.name,
        contentType: draft.file.type || undefined,
        name,
      });
    }

    try {
      await technicalEstimateLegendService.createSymbols(companyId, items);
    } catch (err) {
      console.error("Failed to create legend symbols:", err);
      toast.error(t("configuration.ingenieurslegende.addSymbolsFailedToast"));
      throw err;
    }

    toast.success(t("configuration.ingenieurslegende.symbolsAddedToast"));
    setNewSymbolDialogOpen(false);
    await fetchSymbols();
  };

  const columns: GridColDef[] = [
    {
      field: "symbolImage",
      headerName: t("configuration.ingenieurslegende.symbolColumn"),
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: theme.palette.grey[300],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={params.value as string}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      ),
    },
    {
      field: "description",
      headerName: t("companies.description"),
      flex: 1,
      minWidth: 220,
      display: "flex",
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {params.value as string}
          </Typography>
        </Box>
      ),
    },
    // {
    //   field: "vermogenscode",
    //   headerName: "Vermogenscode",
    //   width: 140,
    //   display: "flex",
    // },
    {
      field: "createdBy",
      headerName: t("configuration.ingenieurslegende.createdByColumn"),
      width: 140,
      display: "flex",
    },
    {
      field: "status",
      headerName: t("common.status"),
      width: 140,
      sortable: false,
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        const status = STATUS_CONFIG[params.value as SymbolStatus];
        return (
          <Tooltip arrow title={status.tooltip}>
            <Chip label={status.label} color={status.color} variant="soft" size="medium" />
          </Tooltip>
        );
      },
    },
    {
      field: "lastUpdated",
      headerName: t("configuration.ingenieurslegende.lastUpdatedColumn"),
      width: 160,
      display: "flex",
    },
    {
      field: "actions",
      headerName: "",
      width: 130,
      sortable: false,
      align: "center",
      headerAlign: "center",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EngineerSymbolRow;

        if (row.status === "pending") {
          const isProcessing = processingId === row.id;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip arrow title={t("configuration.ingenieurslegende.approveTooltip")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isProcessing}
                    onClick={() => handleApprove(row.id)}
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.16) },
                    }}
                  >
                    <TickSquare size={24} variant="Bulk" color={theme.palette.success.main} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip arrow title={t("configuration.ingenieurslegende.rejectSymbol")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isProcessing}
                    onClick={() => setSymbolToReject(row.id)}
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                      "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.16) },
                    }}
                  >
                    <CloseSquare size={24} variant="Bulk" color={theme.palette.error.main} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {row.status !== "rejected" && (
              <Tooltip arrow title={t("common.edit")}>
                <IconButton size="small" onClick={() => setSymbolToRename(row)}>
                  <Edit2 size={20} variant="Outline" color={theme.palette.action.active} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip arrow title={t("common.delete")}>
              <IconButton size="small" onClick={() => setSymbolToDelete(row.id)}>
                <Delete set="curved" stroke="light" primaryColor={theme.palette.error.main} style={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h4" color={theme.palette.primary.dark}>
            {t("configuration.ingenieurslegende.title")}
          </Typography>
          <Chip
            label={t("configuration.ingenieurslegende.symbolsCount", {
              count: rows.length,
            })}
            variant="soft"
            size="medium"
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          size="medium"
          sx={{ borderRadius: "8px", textTransform: "none" }}
          onClick={() => setNewSymbolDialogOpen(true)}
        >
          {t("configuration.ingenieurslegende.newSymbolButton")}
        </Button>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder={t("configuration.ingenieurslegende.searchPlaceholder")} width="509px" />
      </Box>

      <Box
        ref={gridWrapperRef}
        sx={{
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${RADIUS.card}px`,
          boxShadow: theme.customShadows.card,
          flex: 1,
          minHeight: 0,
          width: '100%',
          position: 'relative'
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          slotProps={{
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
          }}
          autoHeight={false}
          height="100%"
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={() => 68}
        />
      </Box>

      <DeleteSymbolDialog
        open={Boolean(symbolToDelete)}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setSymbolToDelete(null)}
      />

      <RejectSymbolDialog
        open={Boolean(symbolToReject)}
        createdBy={rows.find((row) => row.id === symbolToReject)?.createdBy ?? "AI"}
        onConfirm={handleRejectConfirm}
        onCancel={() => setSymbolToReject(null)}
      />

      <RenameSymbolDialog
        open={Boolean(symbolToRename)}
        initialDescription={symbolToRename?.description ?? ""}
        saving={renaming}
        onSave={handleRenameSave}
        onCancel={() => setSymbolToRename(null)}
      />

      <NewSymbolDialog
        open={newSymbolDialogOpen}
        onSave={handleNewSymbolsSave}
        onClose={() => setNewSymbolDialogOpen(false)}
      />
    </Box>
  );
};

export default Ingenieurslegende;
